import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

export default function CartDrawer() {
  const { items, remove, setQty, total, open, setOpen, clear, count } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", note: "" });
  const [checkout, setCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/orders", {
        ...form,
        items: items.map(({ product_id, name, price, quantity }) => ({ product_id, name, price, quantity })),
      });
      toast.success("Inquiry received — our team will confirm your order shortly.");
      clear();
      setCheckout(false);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", organization: "", note: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-transparent border-0 border-b-2 border-ink/15 rounded-none py-3 px-1 text-base focus-visible:ring-0 focus:border-pharma transition-colors";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-stone border-l border-ink/10 w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-ink/10">
          <SheetTitle className="font-serif text-3xl tracking-tight flex items-center gap-3">
            Your Selection
            <span className="font-mono text-xs text-pharma">({count})</span>
          </SheetTitle>
          <SheetDescription className="font-sans text-xs text-ink/50">
            Review your selected products and request an order — our team confirms every order personally.
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-ink/40 px-6">
            <ShoppingBag size={40} strokeWidth={1} />
            <p className="font-sans text-sm">Your selection is empty.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-5" data-lenis-prevent>
              {items.map((i) => (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4">
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-md grayscale-[0.2]" />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <p className="font-serif text-lg leading-tight">{i.name}</p>
                      <p className="font-mono text-xs text-ink/50">${i.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 border border-ink/15 rounded-full px-2 py-1">
                        <button onClick={() => setQty(i.product_id, i.quantity - 1)} data-testid={`cart-dec-${i.product_id}`}><Minus size={13} /></button>
                        <span className="font-mono text-xs w-4 text-center">{i.quantity}</span>
                        <button onClick={() => setQty(i.product_id, i.quantity + 1)} data-testid={`cart-inc-${i.product_id}`}><Plus size={13} /></button>
                      </div>
                      <button onClick={() => remove(i.product_id)} data-testid={`cart-remove-${i.product_id}`} className="text-ink/40 hover:text-destructive transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-ink/10 px-6 py-5">
              <div className="flex justify-between items-baseline mb-4">
                <span className="font-sans text-sm uppercase tracking-widest text-ink/60">Subtotal</span>
                <span data-testid="cart-total" className="font-serif text-2xl">${total.toFixed(2)}</span>
              </div>

              {!checkout ? (
                <button
                  onClick={() => setCheckout(true)}
                  data-testid="cart-checkout-button"
                  className="w-full rounded-full py-4 bg-ink text-stone text-xs uppercase tracking-widest font-sans hover:bg-pharma transition-colors"
                >
                  Request Order
                </button>
              ) : (
                <form onSubmit={submit} className="flex flex-col gap-3" data-testid="cart-checkout-form">
                  <p className="font-sans text-xs text-ink/50">Enter your details — our team confirms every order personally.</p>
                  <Input className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="order-name" />
                  <Input className={field} type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="order-email" />
                  <Input className={field} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="order-phone" />
                  <Input className={field} placeholder="Clinic / Organisation (optional)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="order-org" />
                  <Textarea className={field} placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="order-note" />
                  <button type="submit" disabled={submitting} data-testid="order-submit" className="w-full rounded-full py-4 bg-pharma text-stone text-xs uppercase tracking-widest font-sans hover:bg-pharma-dark transition-colors disabled:opacity-50">
                    {submitting ? "Submitting…" : "Confirm Request"}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
